/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: no any
import React from 'react';
import { ZodOptional, ZodType, ZodTypeDef } from 'zod';
import type { Token } from '@/components/ui/TokenizedInput/types';
import { EntityType } from '@/lib/entities/entityConfig';
import type { CascadingConstraints } from './fields/CascadingProjectReference';

// Base properties common to all field types
export interface FieldMeta {
  label: string;
  placeholder?: string;
  entity?: EntityType;
  options?: { label: string; value: string }[];
  readOnly?: boolean;
  fullWidth?: boolean;
  // visibleWhen?: { field: string; is: any };

  // Optional icon shown beside the label in compact mode
  icon?: React.ComponentType<{ className?: string }>;

  // Filter-related configuration
  filterConfig?: {
    // Icon for the token field (defaults to a generic icon if not provided)
    icon?: React.ComponentType<{ className?: string }>;
    // Override default operations for this field
    operations?: string[];
    // Custom filter function with proper error handling
    filterFn?: (value: any, token: Token) => boolean;
    // Transform function for token value comparison (auto-derived from fieldType if not provided)
    transform?: (value: any) => any;
    // Whether this field should appear in filters (default: true)
    filterable?: boolean;
    // Custom keywords for search
    keywords?: string[];
  };

  // For virtual fields - compute the value from the full entity
  getVirtualValue?: (entity: any, context?: any) => any;
  fieldType:
    | 'text'
    | 'textarea'
    | 'richtext'
    | 'select'
    | 'date'
    | 'boolean'
    | 'foreignKey'
    | 'foreignKeyArray'
    | 'tags' // FIXME: temp field for legacy lists
    | 'hidden'
    | 'custom';
  component?: React.ElementType;

  // For cascading combobox - which levels are allowed
  constraints?: CascadingConstraints;

  // Visibility control - specify which modes the field should appear in
  // If not specified, field is visible in all modes (default behavior)
  visibleIn?: ('view' | 'edit' | 'loading')[];

  // Layout control - when true, renders label above control (stacked)
  // instead of inline. When undefined, defaults based on fieldType
  // (richtext and custom are stacked by default).
  stackedLabel?: boolean;
}

// We need to extend Zod's default type definitions to include our custom meta property
// This makes TypeScript aware of `meta` on any Zod object.
declare module 'zod' {
  export interface ZodTypeDef {
    meta?: FieldMeta;
  }
}

// TODO: Use Zod4 registry instead of meta
/**
 * A typesafe helper function for attaching form-related metadata to a Zod schema.
 * @param schema The Zod schema for the field (e.g., z.string(), z.number()).
 * @param meta The UI-related metadata for the field.
 * @returns The Zod schema with the metadata attached.
 */
export function createField<T extends ZodType>(schema: T, meta: FieldMeta): T {
  (schema.def as ZodTypeDef).meta = meta;
  return schema;
}

/**
 * Utility to immutable change a field
 *
 * This method is causing some problems in ticket bulk-edit-form
 */
export function changeMeta<T extends ZodType, M extends FieldMeta>(
  schema: T,
  partialMeta?: Partial<M>,
): T {
  const clonedSchema = schema.clone(schema._zod.def);
  const def = clonedSchema.def as ZodTypeDef;
  const newDef: ZodTypeDef = {
    ...def,
    meta: partialMeta
      ? {
          ...def.meta!,
          ...partialMeta,
        }
      : def.meta!,
  };
  (clonedSchema._zod.def as ZodTypeDef) = newDef;

  return clonedSchema;
}

/** Utility to make an existing field optional taking care to copy over the meta data
 */
export function makeOptional<T extends ZodType, M extends FieldMeta>(
  schema: T,
  partialMeta?: Partial<M>,
): ZodOptional<T> {
  const optionalSchema = schema.optional();
  (optionalSchema.def as ZodTypeDef).meta = partialMeta
    ? {
        ...(schema.def as ZodTypeDef).meta!,
        ...partialMeta,
      }
    : (schema.def as ZodTypeDef).meta!;
  return optionalSchema;
}

export function getMeta(
  zodSchema: ZodType<unknown, unknown, any>,
): FieldMeta | null {
  return (zodSchema.def as ZodTypeDef).meta || null;
}

/**
 * Helper type to make all fields in a layout optional except specified ones
 */
export type MakeFieldsOptionalExcept<T extends Record<string, any>, E> = {
  [S in keyof T]: {
    [K in keyof T[S]]: K extends E ? T[S][K] : ZodOptional<T[S][K]>;
  };
};

/**
 * Utility function to create a version of a layout with optional fields except specified ones
 * @param layout The original layout object
 * @param requiredFields Array of field names that should remain required
 * @returns A new layout with specified fields required and all others optional
 */
export function makeLayoutOptionalExcept<
  T extends Record<string, Record<string, ZodType>>,
  E extends string,
>(layout: T, requiredFields: E[]): MakeFieldsOptionalExcept<T, E> {
  return Object.fromEntries(
    Object.entries(layout).map(([sectionKey, section]) => [
      sectionKey,
      Object.fromEntries(
        Object.entries(section).map(([fieldKey, field]) => [
          fieldKey,
          requiredFields.includes(fieldKey as E) ? field : makeOptional(field),
        ]),
      ),
    ]),
  ) as MakeFieldsOptionalExcept<T, E>;
}
